// JSON-driven wizard schema
import {StatesService} from '../../services/states/states.service';

export interface FormField {
    type: string;
    name: string;
    label: string;
    placeholder?: string;
    value?: string | number | boolean;
    options?: string[];
    required?: boolean;
    addbutton?: string;
    fields?: any;
    visibilityCondition?: (formData: any) => boolean;
}

export interface WizardPage {
    id: string;
    title: string;
    subtitle?: string;
    fields: FormField[];
    visibilityCondition?: (formData: any) => boolean;
    nextPage?: (formData: any) => string | null;
}

export const wizardPages: WizardPage[] = [
    {
        id: 'startApplication',
        title: 'Start Your Application.',
        subtitle: 'Please enter your information below.',
        fields: [
            {
                type: 'date',
                name: 'dateOfBirth',
                label: 'Date of Birth',
                placeholder: 'MM/DD/YYYY',
                required: true
            },
            {
                type: 'select',
                name: 'residentState',
                label: 'Resident State',
                placeholder: 'Select State',
                required: true,
                options: StatesService.states.map(state => state.abbreviation)
            }
        ],
        nextPage: () => 'startApplication2'
    },
    {
        id: 'startApplication2',
        title: 'Starting Your Application.',
        subtitle: `We'll start with some basic details to set up your account. This step is quick and easy, and you can save your progress anytime. Your information is always safe and secure.`,
        fields: [
            {
                type: 'select',
                name: 'coverageTier',
                label: 'Coverage Tier',
                options: ['Individual', 'Member + Spouse', 'Member + Children', 'Family'],
                required: true
            },
            {
                type: 'text',
                name: 'firstName',
                label: 'First Name',
                required: true
            },
            {
                type: 'text',
                name: 'middleInitial',
                label: 'MI'
            },
            {
                type: 'text',
                name: 'lastName',
                label: 'Last Name',
                required: true
            },
            {
                type: 'text',
                name: 'phoneNumber',
                label: 'Phone Number',
                required: true
            },
            {
                type: 'email',
                name: 'emailAddress',
                label: 'Email Address',
                required: true
            },
            {
                type: 'info',
                name: 'consentNotice',
                label: '',
                value: `By entering an email and phone number, you agree that information is yours. You agree to receive text and email communication about your application. Agreeing to these terms is not a condition of purchase. Msg & data rates may apply. You can opt-out at any time. Disclaimer: By submitting your information you agree that we may contact you at the above listed email or phone number. You understand that consent is not a condition of purchase.`
            },
            {
                type: 'yesno',
                name: 'consent',
                label: '',
                options: ['I agree', 'I don’t agree'],
                required: true
            },

            // Optional additional people based on coverage tier
            {
                type: 'group',
                name: 'spouse',
                label: 'Spouse Information',
                visibilityCondition: (formData) =>
                    formData.coverageTier === 'Member + Spouse' || formData.coverageTier === 'Family',
                fields: [
                    { type: 'text', name: 'spouseFirstName', label: 'First Name', required: true },
                    { type: 'text', name: 'spouseLastName', label: 'Last Name', required: true },
                    { type: 'date', name: 'spouseDOB', label: 'Date of Birth', required: true }
                ]
            },
            {
                type: 'repeatGroup',
                name: 'children',
                label: 'Children',
                addbutton: '+ Add Child',
                visibilityCondition: (formData) =>
                    formData.coverageTier === 'Member + Children' || formData.coverageTier === 'Family',
                fields: [
                    { type: 'text', name: 'childFirstName', label: 'First Name', required: true },
                    { type: 'text', name: 'childLastName', label: 'Last Name', required: true },
                    { type: 'date', name: 'childDOB', label: 'Date of Birth', required: true }
                ]
            }
        ],
        nextPage: () => 'household'
    },
    {
        id: 'household',
        title: 'Your Household.',
        subtitle: `Before we move forward, we need to know a little more about you—who's applying, who you want to cover, and where you're located. This helps us find the best options for your situation.`,
        fields: [
            {
                type: 'select',
                name: 'gender',
                label: 'Gender',
                options: ['Male', 'Female', 'Non-binary', 'Prefer not to say'],
                required: true
            },
            {
                type: 'select',
                name: 'height',
                label: 'Height',
                // 4'0" (48") through 7'0" (84")
                options: Array.from({ length: 37 }, (_, i) => {
                    const inches = i + 48;
                    const ft = Math.floor(inches / 12);
                    const inch = inches % 12;
                    return `${ft}' ${inch}"`;
                }),
                required: true
            },
            {
                type: 'text',
                name: 'weight',
                label: 'Weight (lbs)',
                required: true
            },
            {
                type: 'select',
                name: 'smoker',
                label: 'Smoker',
                options: ['Yes', 'No'],
                required: true
            },
            { type: 'text', name: 'address1', label: 'Address', required: true },
            { type: 'text', name: 'address2', label: 'Address 2' },
            { type: 'text', name: 'city',    label: 'City',    required: true },
            {
                type: 'select',
                name: 'state',
                label: 'State',
                options: StatesService.states.map(s => s.abbreviation),
                required: true
            },
            { type: 'text', name: 'zipCode', label: 'ZIP Code', required: true },
            {
                type: 'select',
                name: 'householdIncomeRange',
                label: 'Please provide your taxable household yearly income.',
                options: [
                    '$0 - $51,520',
                    '$51,521 - $69,680',
                    '$69,681 - $87,840',
                    '$87,841 - $106,000',
                    '$106,001 - $124,160',
                    '$124,161 - $142,320',
                    '$142,321 - $160,480',
                    '$160,481 - $178,640',
                    '$178,641+'
                ],
                required: true
            }
        ],
        nextPage: () => 'firstThings'
    },
    {
        id: 'firstThings',
        title: 'First Things First.',
        subtitle: 'Before we move forward, we need to ask a few key health questions. This helps us understand any recent medical care, current treatments, or special health needs to ensure we fund the best plan for you.',
        fields: [
            {
                type: 'yesno',
                name: 'seenProviderPast5Years',
                label: '1) Within the past 5 years, have you, or anyone listed on this application seen a medical provider for a specific condition and been recommended for treatment, received care, been advised to seek treatment, or undergone diagnostic testing for a condition?'
            },
            {
                type: 'yesno',
                name: 'currentlyDisabledOrInHomeCare',
                label: '2) Are you or anyone listed on this application currently partially or fully disabled, using a wheelchair or motorized aid, hospitalized, bedridden, residing in a nursing facility, receiving hospice or home health care, or otherwise incapacitated?'
            },
            {
                type: 'yesno',
                name: 'takingPrescriptionMedications',
                label: '3) Are you, or anyone listed on this application taking prescription medications, including pills, creams, injections, liquids, inhalers, pumps, oxygen, etc.?'
            },
            {
                type: 'yesno',
                name: 'currentlyPregnant',
                label: '4) Are you, or anyone listed on this application currently pregnant?'
            }
        ],
        nextPage: () => 'specificConditions'
    },
    {
        id: 'specificConditions',
        title: 'Some Specific Health Conditions',
        subtitle: `To keep things simple, we've grouped certain medical conditions together. Let us know if you or anyone on this application has been diagnosed with or treated for any of the conditions listed. This helps us make sure your plan is the right fit.`,
        fields: [
            {
                type: 'yesno',
                name: 'surgeryPlannedOrOngoing',
                label: '1) Surgery that you are still being treated for, or have an upcoming planned surgery.'
            },
            {
                type: 'yesno',
                name: 'heartDisease',
                label: '2) Heart Disease, Heart Attack, Stroke'
            },
            {
                type: 'yesno',
                name: 'mentalHealth',
                label: '3) Mental Health'
            },
            {
                type: 'yesno',
                name: 'diabetes',
                label: '4) Diabetes or Pre-diabetes'
            }
        ],
        nextPage: () => 'surgeryProceduresReview'
    },
    {
        id: 'surgeryProceduresReview',
        title: 'Additional Information Needed.',
        subtitle: `Based on some of your answers, we need a little more info to make sure we’re giving you the best options. If anything needs clarification, we’ll reach out so we can keep things moving smoothly.`,
        fields: [
            {
                type: 'checkbox',
                name: 'surgeryProcedures',
                label: 'Have you or anyone listed on this application seen a medical provider, been recommended for treatment, received care, been advised to seek treatment, undergone diagnostic testing, had surgery, or been hospitalized for any of the conditions listed below? Please select all that apply.',
                options: [
                    'Surgery, medical tests, treatment, or therapy that has not been performed',
                    'Surgery may be required within the next 12 months',
                    'Been hospitalized two or more times in the last 2 years?',
                    'DOES NOT APPLY - I acknowledge I have read the above conditions.'
                ],
                required: true
            }
        ],
        visibilityCondition: formData => formData.surgeryPlannedOrOngoing === true,
        nextPage: () => 'diabetesDetails'
    },
    {
        id: 'diabetesDetails',
        title: 'Diabetes',
        subtitle: 'You answered yes to the Diabetes question. Please provide additional details about those topics.',
        fields: [
            {
                type: 'yesno',
                name: 'hasDiabetes',
                label: '1) Has anyone on this application been diagnosed with diabetes, or are pre-diabetic?',
                required: true
            },
            {
                type: 'radio',  // Changed from checkbox to single-choice
                name: 'diabetesType',
                label: '2) What level of diabetes affects you, or your dependent(s) listed?',
                options: ['Pre-diabetic', 'Type 1', 'Type 2', 'Gestational Diabetes', 'Other'],
                required: true,
                visibilityCondition: (data) => data.hasDiabetes === true
            },
            {
                type: 'checkbox',
                name: 'diabetesComplications',
                label: 'For the individual(s) in question, please select all that apply. You may have multiple selections.',
                options: [
                    'Retinopathy (vision complications)',
                    'Neuropathy (neurological complications)',
                    'Heart disease of any type',
                    'More than 50 units of insulin daily',
                    'Three or more medications to treat diabetes (insulin and oral)',
                    'Three or more medications to treat high blood pressure',
                    'DOES NOT APPLY - I acknowledge I have read the above conditions.'
                ],
                visibilityCondition: (data) => data.hasDiabetes === true
            }
        ],
        visibilityCondition: formGroup => formGroup.get('diabetes').value === true,
        nextPage: () => 'heartdiseaseDetails'
    },
    {
        id: 'heartDiseaseDetails',
        title: 'Heart Disease',
        subtitle: 'Have you or anyone listed on this application seen a medical provider, been recommended for treatment, received care, been advised to seek treatment, undergone diagnostic testing, had surgery, or been hospitalized for any of the conditions listed below? Please select all that apply, you may have multiple selections',
        fields: [
            {
                type: 'checkbox',
                name: 'heartConditions',
                label: '',
                options: [
                    'Angioplasty',
                    'Heart attack',
                    'Bypass',
                    'Heart disorder/other',
                    'Carotid artery disease',
                    'Heart valve surgery including replacement',
                    'Chest Pain',
                    'TIA (Transient Ischemic Attack)',
                    'Congestive heart failure enlarged heart',
                    'Use of heart pacemaker or defibrillator stroke',
                    'Coronary artery disease',
                    'Vascular disorders, including peripheral vascular disease',
                    'DOES NOT APPLY - I acknowledge I have read the above conditions.'
                ],
                required: true
            }
        ],
        visibilityCondition: formData => formData.heartDisease === true,
        nextPage: () => 'mentalHealthDetails'
    },
    {
        id: 'mentalHealthDetails',
        title: 'Mental Health',
        subtitle: 'Have you or anyone listed on this application seen a medical provider, been recommended for treatment, received care, been advised to seek treatment, undergone diagnostic testing, had surgery, or been hospitalized for any of the conditions listed below? Please select all that apply, you may have multiple selections',
        fields: [
            {
                type: 'checkbox',
                name: 'mentalHealthConditions',
                label: '',
                options: [
                    'Mental, Nervous or Behavioral Disorder',
                    'Mental Health Inpatient or Outpatient Treatment',
                    'Mental or nervous disorder requiring psychiatric hospitalization',
                    'Bipolar Disorder',
                    'Alcohol or Drug Use, Abuse or Dependency',
                    'Anxiety/Depression',
                    'DOES NOT APPLY - I acknowledge I have read the above conditions.'
                ],
                required: true
            }
        ],
        visibilityCondition: formData => formData.mentalHealth === true,
        nextPage: () => 'medicationDetails'
    },
    {
        id: 'medicationDetails',
        title: 'Medication Details.',
        subtitle: 'Tell us about any prescription medications you or anyone on this application are currently taking. This helps us understand ongoing treatment and ensure your plan meets your needs.',
        fields: [
            {
                type: 'repeatGroup',
                name: 'medications',
                label: 'Medications',
                fields: [
                    {
                        type: 'select',
                        name: 'member',
                        label: 'Select member',
                        options: ['Individual', 'Spouse', 'Child'],
                        required: true
                    },
                    {
                        type: 'text',
                        name: 'medicationName',
                        label: 'Medication Name',
                        required: true
                    },
                    {
                        type: 'text',
                        name: 'dosageAmount',
                        label: 'Dosage Amount'
                    },
                    {
                        type: 'text',
                        name: 'frequency',
                        label: 'Frequency'
                    },
                    {
                        type: 'textarea',
                        name: 'treatmentIssue',
                        label: 'Describe Treatment / Issue'
                    }
                ]
            }
        ],
        visibilityCondition: formData => formData.formdatatakingPrescriptionMedications === true,
        nextPage: () => 'healthConditionsExpanded'
    },
    {
        id: 'healthConditionsExpanded',
        title: 'Health Conditions.',
        subtitle: `To keep things simple, we've grouped certain medical conditions together. Let us know if you or anyone on this application has been diagnosed with or treated for any of the conditions listed. This helps us make sure your plan is the right fit.`,
        fields: [
            {
                type: 'checkbox',
                name: 'healthConditionsExpanded',
                label: 'Have you or anyone listed on this application seen a medical provider, been recommended for treatment, received care, been advised to seek treatment, undergone diagnostic testing, had surgery, or been hospitalized for any of the conditions listed below? Please select all that apply. You may have multiple selections.',
                options: [
                    'AIDS, ARC or HIV infection',
                    'ALS (amyotrophic lateral sclerosis)',
                    'Alzheimer\'s disease',
                    'Amputation caused by disease',
                    'Chronic Pulmonary disorder',
                    'Emphysema',
                    'Lou Gehrig\'s disease systemic lupus',
                    'Myasthenia gravis',
                    'Multiple sclerosis',
                    'Organ Transplant',
                    'Other Cognitive Disorder',
                    'Parkinson\'s disease',
                    'Senile dementia',
                    'Use of supplemental oxygen or a nebulizer to treat a pulmonary/respiratory disorder',
                    'DOES NOT APPLY - I acknowledge I have read the above conditions.'
                ],
                required: true
            }
        ],
        visibilityCondition: formData => formData.seenProviderPast5Years === true,
        nextPage: () => 'otherConditionsExpanded'
    },
    {
        id: 'otherConditionsExpanded',
        title: 'Other Conditions.',
        subtitle: `You're almost there! Before we wrap up, we just need to cover all our bases. Let us know if you or anyone on this application has had any of the conditions listed below to ensure we get you the best possible plan.`,
        fields: [
            {
                type: 'checkbox',
                name: 'otherConditionsExpanded',
                label: 'Have you or anyone listed on this application seen a medical provider, been recommended for treatment, received care, been advised to seek treatment, undergone diagnostic testing, had surgery, or been hospitalized for any of the conditions listed below? Please select all that apply, you may have multiple selections.',
                options: [
                    'ADHD or ADD',
                    'Alcohol or Drug Use, Abuse or Dependency',
                    'Anxiety/Depression',
                    'Asthma',
                    'Blood Disorders (including anemia), Immune Disorders, Endocrine Disorders, Thyroid Disorders of any kind',
                    'Cancer, Tumor, Melanoma - Any type',
                    'Cirrhosis',
                    'COPD',
                    'Digestive Disorder, Crohns Disease, Urinary Disorder, Ulcerative Colitis',
                    'Ear/Eye/Nose/Throat Disorders',
                    'Fracture or Broken Bone, Knee Injury or Disorder, Back / Spinal / Skeletal Disorder',
                    'Growth Disorder, Physical Disorder or Deformity, Paralysis',
                    'Hepatitis B, C, D',
                    'Infertility, or other Reproductive Disorder',
                    'Kidney or Liver Disorder or Disease, including end stage renal disease, renal failure',
                    'Osteoarthritis, Rheumatoid Arthritis, or other Arthritis',
                    'Nervous System Disorder, Migraine or Chronic Headache',
                    'Seizures',
                    'Sexually Transmitted Disease',
                    'DOES NOT APPLY - I acknowledge I have read the above conditions.'
                ],
                required: true
            }
        ],
        nextPage: () => 'conditionsNotListed'
    },
    {
        id: 'conditionsNotListed',
        title: 'Conditions Not Listed.',
        subtitle: `Have a condition that wasn't specifically asked about? Let us know here so we can get the full picture and ensure you receive the best possible options.`,
        fields: [
            {
                type: 'yesno',
                name: 'hasOtherConditions',
                label: 'Within the past 5 years, have you, or anyone listed on this application been diagnosed or treated for any condition(s) not identified so far; or been advised of the necessity or possibility of any future hospitalization, treatment, testing or surgery?',
                required: true
            },
            {
                type: 'textarea',
                name: 'otherConditionDetails',
                label: 'Please describe: name of individual, date on onset, treatment plan, degree of recovery, still treating or last date of treatment.',
                placeholder: 'Enter details here...',
                visibilityCondition: formData => formData.hasOtherConditions === true
            }
        ],
        nextPage: () => 'acknowledgement'
    },
    {
        id: 'acknowledgement',
        title: 'Acknowledgement',
        fields: [
            { type: 'checkbox', name: 'confirmInfo', label: 'I confirm the information is true' },
            { type: 'text', name: 'signature', label: 'Signature' },
            { type: 'date', name: 'signatureDate', label: 'Date' }
        ],
        nextPage: () => null
    }
];
